// src/admin/components/sales/SaleForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    RiUserLine, RiSaveLine, RiArrowLeftLine, RiErrorWarningLine,
    RiBuildingLine, RiMoneyDollarCircleLine, RiCheckboxCircleLine, RiCloseCircleLine,
    RiSearchLine, RiArrowDownSLine, RiLayoutMasonryLine, RiHandCoinLine
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import { createSale } from '../../../models/saleModel';
import { getAllApartments } from '../../../models/apartmentModel';
import toast from 'react-hot-toast';
import PrintReceipt from '../payments/PrintReceipt';

const SaleForm = () => {
    const { isDarkMode } = useTheme();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isRTL = ['ar', 'ku'].includes(i18n.language);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [availableApartments, setAvailableApartments] = useState([]);
    const [defaultCountry, setDefaultCountry] = useState('iq');

    // showroom picker state
    const [showroomSearch, setShowroomSearch] = useState('');
    const [showroomTypeFilter, setShowroomTypeFilter] = useState('all');
    const [expandedZones, setExpandedZones] = useState(new Set());

    // Print receipt state
    const [showReceipt, setShowReceipt] = useState(false);
    const [createdSale, setCreatedSale] = useState(null);

    const today = new Date().toISOString().split('T')[0];

    const buildDates = (count, base) => Array.from({ length: count }, (_, i) => {
        const d = new Date(base + 'T00:00:00');
        d.setMonth(d.getMonth() + i);
        return d.toISOString().split('T')[0];
    });

    const [form, setForm] = useState({
        buyerName: '',
        buyerPhone: '',
        buyerLanguage: 'en',
        apartmentId: '',
        totalPrice: '',
        paymentType: 'full',
        numInstallments: 3,
        firstPaymentDate: today,
        installmentDates: buildDates(3, today),
        installmentAmounts: [],
        customAmounts: false,
        notes: '',
        markAsPaid: false,
        livingThere: false,
        hasCommission: false,
        commissionType: 'percent', // 'percent' | 'fixed'
        commissionRate: '',
        commissionAmount: '',
        commissionNotes: ''
    });

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                fetch('https://ipapi.co/json/')
                    .then(r => r.json())
                    .then(d => { if (d.country_code) setDefaultCountry(d.country_code.toLowerCase()); })
                    .catch(() => {});
                try {
                    const res = await getAllApartments({ withoutOwner: 'true' });
                    setAvailableApartments(res.apartments || []);
                } catch {
                    const res = await getAllApartments();
                    setAvailableApartments((res.apartments || []).filter(a => !a.ownerId));
                }
            } catch {
                setError(t('failed_to_load_sales'));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [t]);

    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const setNumInstallments = (n) => {
        const count = Math.max(2, Math.min(120, parseInt(n) || 2));
        setForm(prev => {
            const base = prev.installmentDates[0] || prev.firstPaymentDate || today;
            const dates = Array.from({ length: count }, (_, i) => {
                if (prev.installmentDates[i]) return prev.installmentDates[i];
                const d = new Date(base + 'T00:00:00');
                d.setMonth(d.getMonth() + i);
                return d.toISOString().split('T')[0];
            });
            const amounts = Array.from({ length: count }, (_, i) => prev.installmentAmounts[i] ?? '');
            return { ...prev, numInstallments: count, installmentDates: dates, installmentAmounts: amounts };
        });
    };

    const setInstallmentDate = (index, val) => {
        setForm(prev => {
            const dates = [...prev.installmentDates];
            dates[index] = val;
            return { ...prev, installmentDates: dates };
        });
    };

    const setInstallmentAmount = (index, val) => {
        setForm(prev => {
            const amounts = [...prev.installmentAmounts];
            amounts[index] = val;
            return { ...prev, installmentAmounts: amounts };
        });
    };

    const toggleCustomAmounts = (checked) => {
        setForm(prev => {
            if (checked) {
                // Seed custom amounts with the current equal-split values
                const equalAmt = prev.totalPrice ? Math.round((parseFloat(prev.totalPrice) / prev.numInstallments) * 100) / 100 : 0;
                const amounts = Array.from({ length: prev.numInstallments }, (_, i) => {
                    if (i === prev.numInstallments - 1) {
                        // last installment absorbs rounding remainder
                        const sumSoFar = equalAmt * (prev.numInstallments - 1);
                        return Math.round(((parseFloat(prev.totalPrice) || 0) - sumSoFar) * 100) / 100;
                    }
                    return equalAmt;
                });
                return { ...prev, customAmounts: true, installmentAmounts: amounts };
            }
            return { ...prev, customAmounts: false, installmentAmounts: [] };
        });
    };

    // Sum of custom installment amounts (only meaningful when customAmounts is true)
    const customAmountsTotal = useMemo(() => {
        if (!form.customAmounts) return 0;
        return form.installmentAmounts.reduce((sum, a) => sum + (parseFloat(a) || 0), 0);
    }, [form.customAmounts, form.installmentAmounts]);

    const customAmountsRemaining = useMemo(() => {
        const total = parseFloat(form.totalPrice) || 0;
        return Math.round((total - customAmountsTotal) * 100) / 100;
    }, [form.totalPrice, customAmountsTotal]);

    // Group available apartments by floor (= zone), sorted numerically
    const groupedByZone = useMemo(() => {
        const groups = {};
        availableApartments.forEach(apt => {
            const zone = apt.floor || 1;
            if (!groups[zone]) groups[zone] = [];
            groups[zone].push(apt);
        });
        // sort units within each zone numerically
        Object.values(groups).forEach(arr =>
            arr.sort((a, b) => {
                const na = parseInt(a.apartmentNumber);
                const nb = parseInt(b.apartmentNumber);
                return isNaN(na) || isNaN(nb)
                    ? a.apartmentNumber.localeCompare(b.apartmentNumber)
                    : na - nb;
            })
        );
        // return sorted zone keys
        return Object.keys(groups)
            .sort((a, b) => Number(a) - Number(b))
            .map(zk => ({ zone: zk, units: groups[zk] }));
    }, [availableApartments]);

    // Collect distinct types for filter dropdown
    const distinctTypes = useMemo(() => {
        const s = new Set(availableApartments.map(a => a.type).filter(Boolean));
        return [...s].sort();
    }, [availableApartments]);

    // Filter groups by search + type
    const filteredGroups = useMemo(() => {
        const q = showroomSearch.trim().toLowerCase();
        return groupedByZone.map(({ zone, units }) => ({
            zone,
            units: units.filter(apt => {
                const matchSearch = !q ||
                    String(apt.apartmentNumber).toLowerCase().includes(q) ||
                    (apt.buildingName || '').toLowerCase().includes(q) ||
                    String(apt.floor || '').toLowerCase().includes(q) ||
                    (apt.type || '').toLowerCase().includes(q);
                const matchType = showroomTypeFilter === 'all' || apt.type === showroomTypeFilter;
                return matchSearch && matchType;
            })
        })).filter(g => g.units.length > 0);
    }, [groupedByZone, showroomSearch, showroomTypeFilter]);

    // Auto-expand first zone on first load
    useEffect(() => {
        if (groupedByZone.length > 0 && expandedZones.size === 0) {
            setExpandedZones(new Set([groupedByZone[0].zone]));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupedByZone]);

    const toggleZone = (zone) => {
        setExpandedZones(prev => {
            const n = new Set(prev);
            n.has(zone) ? n.delete(zone) : n.add(zone);
            return n;
        });
    };

    const selectedApt = availableApartments.find(a => a.id === form.apartmentId);

    // Resolved commission amount — computed the same way the backend will compute/validate it
    const computedCommissionAmount = useMemo(() => {
        if (!form.hasCommission) return 0;
        const price = parseFloat(form.totalPrice) || 0;
        if (form.commissionType === 'percent') {
            const rate = parseFloat(form.commissionRate) || 0;
            return Math.round(price * (rate / 100) * 100) / 100;
        }
        return Math.round((parseFloat(form.commissionAmount) || 0) * 100) / 100;
    }, [form.hasCommission, form.commissionType, form.commissionRate, form.commissionAmount, form.totalPrice]);

    const netToCompanyPreview = useMemo(() => {
        const price = parseFloat(form.totalPrice) || 0;
        return Math.round((price - computedCommissionAmount) * 100) / 100;
    }, [form.totalPrice, computedCommissionAmount]);

    const validate = () => {
        if (!form.buyerName.trim()) { setError(t('buyer_name_required')); return false; }
        if (!form.buyerPhone || form.buyerPhone.length < 7) { setError(t('valid_phone_required')); return false; }
        if (!form.apartmentId) { setError(t('apartment_required_sale')); return false; }
        if (!form.totalPrice || isNaN(form.totalPrice) || parseFloat(form.totalPrice) <= 0) { setError(t('valid_price_required')); return false; }
        if (form.paymentType === 'full' && !form.firstPaymentDate) { setError(t('first_payment_date_required')); return false; }
        if (form.paymentType === 'installments' && form.installmentDates.some(d => !d)) {
            setError(t('first_payment_date_required')); return false;
        }
        if (form.paymentType === 'installments' && form.customAmounts) {
            if (form.installmentAmounts.some(a => a === '' || isNaN(a) || parseFloat(a) <= 0)) {
                setError(t('valid_installment_amounts_required')); return false;
            }
            if (Math.abs(customAmountsRemaining) > 0.01) {
                setError(t('installment_amounts_must_match_total')); return false;
            }
        }
        if (form.hasCommission) {
            if (form.commissionType === 'percent') {
                const rate = parseFloat(form.commissionRate);
                if (form.commissionRate === '' || isNaN(rate) || rate < 0 || rate > 100) {
                    setError(t('valid_commission_rate_required') || 'Commission rate must be between 0 and 100'); return false;
                }
            } else {
                const amt = parseFloat(form.commissionAmount);
                if (form.commissionAmount === '' || isNaN(amt) || amt < 0) {
                    setError(t('valid_commission_amount_required') || 'Commission amount must be a valid non-negative number'); return false;
                }
                if (amt > (parseFloat(form.totalPrice) || 0)) {
                    setError(t('commission_exceeds_price') || 'Commission amount cannot exceed the total sale price'); return false;
                }
            }
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!validate()) return;
        try {
            setSaving(true);
            const response = await createSale({
                ...form,
                totalPrice: parseFloat(form.totalPrice),
                numInstallments: parseInt(form.numInstallments),
                installmentDates: form.paymentType === 'installments' ? form.installmentDates : undefined,
                installmentAmounts: form.paymentType === 'installments' && form.customAmounts
                    ? form.installmentAmounts.map(a => parseFloat(a))
                    : undefined,
                commissionType: form.hasCommission ? form.commissionType : null,
                commissionRate: form.hasCommission && form.commissionType === 'percent' ? parseFloat(form.commissionRate) : null,
                commissionAmount: form.hasCommission && form.commissionType === 'fixed' ? parseFloat(form.commissionAmount) : null,
                commissionNotes: form.hasCommission ? form.commissionNotes : '',
            });
            
            toast.success(t('sale_recorded_success'));
            
            if (response.success && response.sale) {
                // Prepare sale data for receipt
                const saleForPrint = {
                    ...response.sale,
                    paymentType: 'sale',
                    preferredLanguage: form.buyerLanguage 
                };
                setCreatedSale(saleForPrint);
                setShowReceipt(true);
            } else {
                navigate('/admin/sales');
            }
        } catch (err) {
            setError(err.message || t('failed_to_create_sale'));
        } finally {
            setSaving(false);
        }
    };

    const inputClass = `w-full p-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-red-500
        ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-500' : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400'}`;
    const labelClass = `block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`;
    const sectionClass = `p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-zinc-750' : 'bg-zinc-50'}`;

    if (loading) return (
        <div className="flex items-center justify-center py-24">
            <div className="animate-spin h-8 w-8 border-4 border-red-500 border-t-transparent rounded-full" />
        </div>
    );

    return (
        <div className={`${isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'} rounded-xl border shadow-lg`} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className={`p-5 border-b ${isDarkMode ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <RiMoneyDollarCircleLine className={`me-3 text-2xl ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                        <div>
                            <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                {t('record_new_sale')}
                            </h1>
                            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                {t('record_new_sale_subtitle')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/admin/sales')}
                        className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-zinc-100 hover:bg-zinc-200'} text-zinc-600 transition-colors`}
                    >
                        <RiArrowLeftLine size={20} className="rtl:rotate-180" />
                    </button>
                </div>
            </div>

            {error && (
                <div className={`mx-5 mt-5 p-4 rounded-lg flex items-center gap-3 ${isDarkMode ? 'bg-red-900/20 border border-red-800 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                    <RiErrorWarningLine size={18} className="flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                </div>
            )}

            <form onSubmit={handleSubmit} className="p-5">
                {/* ── Section 1: Buyer Info ── */}
                <div className={sectionClass}>
                    <h2 className={`text-base font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                        <RiUserLine className="text-red-500" />
                        {t('buyer_information')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>{t('buyer_name')} <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={form.buyerName}
                                onChange={e => set('buyerName', e.target.value)}
                                placeholder={t('buyer_name_placeholder')}
                                className={inputClass}
                                required
                            />
                        </div>
                        <div>
                            <label className={labelClass}>{t('buyer_phone')} <span className="text-red-500">*</span></label>
                            <div dir="ltr">
                                <PhoneInput
                                    country={defaultCountry}
                                    value={form.buyerPhone}
                                    onChange={phone => set('buyerPhone', '+' + phone)}
                                    enableSearch
                                    disableSearchIcon
                                    buttonStyle={{ backgroundColor: isDarkMode ? '#3f3f46' : '#f9fafb', borderColor: isDarkMode ? '#52525b' : '#d1d5db' }}
                                    inputStyle={{ width: '100%', backgroundColor: isDarkMode ? '#3f3f46' : '#ffffff', borderColor: isDarkMode ? '#52525b' : '#d1d5db', color: isDarkMode ? '#fff' : '#111827' }}
                                    dropdownStyle={{ backgroundColor: isDarkMode ? '#27272a' : '#ffffff', color: isDarkMode ? '#fff' : '#111827' }}
                                />
                            </div>
                        </div>
                        <div>
                            <label className={labelClass}>{t('buyer_language')}</label>
                            <select value={form.buyerLanguage} onChange={e => set('buyerLanguage', e.target.value)} className={inputClass}>
                                <option value="ku">کوردی</option>
                                <option value="en">English</option>
                                <option value="ar">العربية</option>

                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>{t('notes_optional')}</label>
                            <input
                                type="text"
                                value={form.notes}
                                onChange={e => set('notes', e.target.value)}
                                placeholder={t('notes_placeholder')}
                                className={inputClass}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg border ${isDarkMode ? 'border-zinc-600 bg-zinc-700 hover:bg-zinc-600' : 'border-zinc-200 bg-white hover:bg-zinc-50'} transition-colors`}>
                                <input
                                    type="checkbox"
                                    checked={form.livingThere}
                                    onChange={e => set('livingThere', e.target.checked)}
                                    className="w-4 h-4 accent-red-600"
                                />
                                <span className={`text-sm font-medium ${isDarkMode ? 'text-zinc-200' : 'text-zinc-700'}`}>
                                    {t('works_at_showroom') || 'Buyer works at this showroom'}
                                </span>
                                {form.livingThere && (
                                    <span className={`ms-auto text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
                                        {t('monthly_service_fee_applies') || 'Monthly service fee applies'}
                                    </span>
                                )}
                            </label>
                        </div>
                    </div>
                </div>

                {/* ── Section 2: Select Showroom ── */}
                <div className={sectionClass}>
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-4">
                        <h2 className={`text-base font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                            <RiBuildingLine className="text-red-500" />
                            {t('select_apartment_required')}
                        </h2>
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                            {t('available_apartments_count', { count: availableApartments.length })}
                        </span>
                    </div>

                    {availableApartments.length === 0 ? (
                        <div className={`py-8 text-center rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
                            <RiBuildingLine size={32} className="mx-auto mb-2 text-zinc-400" />
                            <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                {t('no_apartments_available_sale')}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {/* ── Selected unit badge ── */}
                            {selectedApt && (
                                <div className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border ${
                                    isDarkMode ? 'bg-red-900/20 border-red-700/50' : 'bg-red-50 border-red-200'
                                }`}>
                                    <RiCheckboxCircleLine className="text-red-500 shrink-0" size={16} />
                                    <span className={`text-sm font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                                        #{selectedApt.apartmentNumber}
                                    </span>
                                    <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                        · {selectedApt.buildingName} · Zone {selectedApt.floor} · {selectedApt.type} · {selectedApt.size} m²
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => set('apartmentId', '')}
                                        className={`ms-auto text-xs px-2 py-0.5 rounded ${isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-800'}`}
                                    >
                                        {t('change', 'Change')}
                                    </button>
                                </div>
                            )}

                            {/* ── Search + Type filter toolbar ── */}
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <RiSearchLine
                                        size={15}
                                        className={`absolute top-1/2 -translate-y-1/2 start-3 ${
                                            isDarkMode ? 'text-zinc-500' : 'text-zinc-400'
                                        }`}
                                    />
                                    <input
                                        type="text"
                                        value={showroomSearch}
                                        onChange={e => setShowroomSearch(e.target.value)}
                                        placeholder={t('search_showroom_placeholder', 'Search by unit #, zone, type…')}
                                        className={`w-full ps-9 pe-3 py-2 text-sm rounded-lg border outline-none focus:ring-2 focus:ring-red-500/40 ${
                                            isDarkMode
                                                ? 'bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-500'
                                                : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400'
                                        }`}
                                    />
                                </div>
                                <select
                                    value={showroomTypeFilter}
                                    onChange={e => setShowroomTypeFilter(e.target.value)}
                                    className={`text-sm px-3 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-red-500/40 ${
                                        isDarkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-zinc-300 text-zinc-900'
                                    }`}
                                >
                                    <option value="all">{t('all_types', 'All Types')}</option>
                                    {distinctTypes.map(tp => (
                                        <option key={tp} value={tp}>{tp}</option>
                                    ))}
                                </select>
                            </div>

                            {/* ── Zone accordion ── */}
                            {filteredGroups.length === 0 ? (
                                <div className={`py-6 text-center rounded-lg border-2 border-dashed ${
                                    isDarkMode ? 'border-zinc-700 text-zinc-500' : 'border-zinc-200 text-zinc-400'
                                }`}>
                                    <p className="text-sm">{t('no_showrooms_match_search', 'No showrooms match your search.')}</p>
                                    <button
                                        type="button"
                                        onClick={() => { setShowroomSearch(''); setShowroomTypeFilter('all'); }}
                                        className="mt-2 text-xs text-red-500 hover:underline"
                                    >
                                        {t('clear_filters', 'Clear filters')}
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2 max-h-80 overflow-y-auto pe-1">
                                    {filteredGroups.map(({ zone, units }) => {
                                        const isExpanded = expandedZones.has(zone);
                                        const vacantCt = units.filter(a => a.status === 'vacant').length;
                                        return (
                                            <div
                                                key={zone}
                                                className={`rounded-xl border overflow-hidden transition-shadow ${
                                                    isDarkMode ? 'bg-zinc-800/60 border-zinc-700' : 'bg-white border-zinc-200'
                                                } ${isExpanded ? 'shadow-md' : 'shadow-sm hover:shadow-md'}`}
                                            >
                                                {/* Zone header / toggle */}
                                                <button
                                                    type="button"
                                                    onClick={() => toggleZone(zone)}
                                                    className={`w-full text-start px-4 py-3 flex items-center justify-between transition-colors ${
                                                        isDarkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-zinc-50'
                                                    } ${isExpanded ? `border-b ${ isDarkMode ? 'border-zinc-700' : 'border-zinc-100'}` : ''}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-bold text-xs shrink-0 ${
                                                            isDarkMode ? 'bg-zinc-700 border border-zinc-600 text-zinc-300' : 'bg-zinc-100 border border-zinc-200 text-zinc-700'
                                                        }`}>
                                                            <RiLayoutMasonryLine className={isDarkMode ? 'text-red-400' : 'text-red-500'} size={13}/>
                                                            {t('zone', 'ZONE')} {zone}
                                                        </div>
                                                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                                                            {units.length} {t('units', 'units')}
                                                        </span>
                                                        {vacantCt > 0 && (
                                                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500/10 text-green-400 border border-green-500/20">
                                                                {vacantCt} Vacant
                                                            </span>
                                                        )}
                                                    </div>
                                                    <RiArrowDownSLine
                                                        size={18}
                                                        className={`transition-transform duration-250 ${
                                                            isExpanded ? 'rotate-180' : ''
                                                        } ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}
                                                    />
                                                </button>

                                                {/* Zone units — BACK ROW / FRONT ROW layout (matches Complex View) */}
                                                {isExpanded && (() => {
                                                    const sorted = [...units].sort((a, b) => {
                                                        const na = parseInt(a.apartmentNumber);
                                                        const nb = parseInt(b.apartmentNumber);
                                                        return isNaN(na) || isNaN(nb)
                                                            ? a.apartmentNumber.localeCompare(b.apartmentNumber)
                                                            : na - nb;
                                                    });
                                                    const hasPosData = sorted.some(a => a.rowPosition);
                                                    let frontRow, backRow, cornerUnits;
                                                    if (hasPosData) {
                                                        frontRow    = sorted.filter(a => a.rowPosition === 'front');
                                                        backRow     = [...sorted.filter(a => a.rowPosition === 'back')].reverse();
                                                        cornerUnits = sorted.filter(a => a.rowPosition === 'corner');
                                                    } else {
                                                        const half = Math.floor(sorted.length / 2);
                                                        frontRow    = sorted.slice(0, half);
                                                        backRow     = [...sorted.slice(half)].reverse();
                                                        cornerUnits = [];
                                                    }

                                                    const UnitCard = ({ apt }) => {
                                                        const selected = form.apartmentId === apt.id;
                                                        const statusColor = apt.status === 'vacant' ? '#3b82f6' : '#f59e0b';
                                                        return (
                                                            <div
                                                                onClick={() => set('apartmentId', apt.id)}
                                                                className={`relative flex flex-col rounded-xl border-2 overflow-hidden cursor-pointer select-none transition-all hover:-translate-y-1 shrink-0 w-[110px]
                                                                    ${selected
                                                                        ? isDarkMode ? 'border-red-500 bg-red-900/20 shadow-lg shadow-red-900/30' : 'border-red-500 bg-red-50'
                                                                        : isDarkMode ? 'border-zinc-700 bg-zinc-800 hover:border-zinc-500 hover:shadow-lg' : 'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-md'
                                                                    }`}
                                                            >
                                                                {/* Status top bar */}
                                                                <div className="absolute inset-x-0 top-0 h-1 rounded-t-xl"
                                                                    style={{ backgroundColor: selected ? '#ef4444' : statusColor }} />
                                                                <div className="p-2.5 pt-3.5 flex flex-col gap-1">
                                                                    <div className="flex items-start justify-between">
                                                                        <span className={`font-mono font-black text-lg leading-none ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                                                            {apt.apartmentNumber}
                                                                        </span>
                                                                        {selected
                                                                            ? <RiCheckboxCircleLine className="text-red-500 shrink-0" size={15} />
                                                                            : <span className="text-[8px] font-bold uppercase px-1 py-0.5 rounded"
                                                                                style={{ backgroundColor: `${statusColor}20`, color: statusColor, border: `1px solid ${statusColor}30` }}>
                                                                                {t(apt.status, apt.status)}
                                                                              </span>
                                                                        }
                                                                    </div>
                                                                    {apt.type && (
                                                                        <span className={`self-start text-[8px] font-bold uppercase px-1.5 py-0.5 rounded border ${
                                                                            isDarkMode ? 'bg-blue-500/20 text-blue-300 border-blue-500/40' : 'bg-blue-50 text-blue-600 border-blue-200'
                                                                        }`}>
                                                                            {t(`type_${apt.type.toLowerCase().replace(/\\s+/g, '_')}`, apt.type)}
                                                                        </span>
                                                                    )}
                                                                    {apt.size && (
                                                                        <p className={`text-[9px] mt-0.5 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                                                                            {apt.size} m²
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    };

                                                    return (
                                                        <div className={`p-3 ${isDarkMode ? 'bg-zinc-900/30' : 'bg-zinc-50/50'}`}>
                                                            <div className="overflow-x-auto">
                                                                <div className={`inline-flex gap-3 p-3 rounded-2xl border min-w-max ${
                                                                    isDarkMode ? 'bg-zinc-900/60 border-zinc-700/60' : 'bg-zinc-50/80 border-zinc-200'
                                                                }`}>
                                                                    {/* Main block: back + divider + front */}
                                                                    <div className="flex flex-col gap-2">
                                                                        {/* ↑ BACK ROW */}
                                                                        <div className="flex flex-col gap-1.5">
                                                                            <div className="flex items-center gap-2 mb-1">
                                                                                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                                                                                    isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-zinc-200 text-zinc-500'
                                                                                }`}>↑ {t('back_row', 'Back Row')}</span>
                                                                            </div>
                                                                            <div className={`flex gap-2 px-3 py-2 rounded-xl border border-dashed ${
                                                                                isDarkMode ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-300 bg-white/60'
                                                                            }`}>
                                                                                {backRow.map(apt => <UnitCard key={apt.id} apt={apt} />)}
                                                                            </div>
                                                                        </div>
                                                                        {/* Wall divider */}
                                                                        <div className="flex items-center gap-2 px-2">
                                                                            <div className={`flex-1 h-2 rounded-full ${isDarkMode ? 'bg-zinc-600' : 'bg-zinc-300'}`} />
                                                                            <span className={`text-[9px] font-bold uppercase tracking-widest shrink-0 ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>
                                                                                ─ {t('wall', 'wall')} ─
                                                                            </span>
                                                                            <div className={`flex-1 h-2 rounded-full ${isDarkMode ? 'bg-zinc-600' : 'bg-zinc-300'}`} />
                                                                        </div>
                                                                        {/* ↓ FRONT ROW */}
                                                                        <div className="flex flex-col gap-1.5">
                                                                            <div className="flex items-center gap-2 mb-1">
                                                                                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                                                                                    isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-zinc-200 text-zinc-500'
                                                                                }`}>↓ {t('front_row', 'Front Row')}</span>
                                                                            </div>
                                                                            <div className={`flex gap-2 px-3 py-2 rounded-xl border border-dashed ${
                                                                                isDarkMode ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-300 bg-white/60'
                                                                            }`}>
                                                                                {frontRow.map(apt => <UnitCard key={apt.id} apt={apt} />)}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    {/* Corner column */}
                                                                    {cornerUnits.length > 0 && (
                                                                        <div className={`flex flex-col gap-2 justify-center ps-3 border-s ${
                                                                            isDarkMode ? 'border-zinc-700' : 'border-zinc-300'
                                                                        }`}>
                                                                            <span className={`text-[10px] font-bold uppercase tracking-widest text-center ${
                                                                                isDarkMode ? 'text-zinc-500' : 'text-zinc-400'
                                                                            }`}>{t('corner', 'Corner')}</span>
                                                                            {[...cornerUnits].reverse().map(apt => (
                                                                                <UnitCard key={apt.id} apt={apt} />
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Section 3: Payment Plan ── */}
                <div className={sectionClass}>
                    <h2 className={`text-base font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                        <RiMoneyDollarCircleLine className="text-red-500" />
                        {t('payment_plan')}
                    </h2>

                    <div className="flex gap-3 mb-5">
                        {[
                            { value: 'full', key: 'full_payment' },
                            { value: 'installments', key: 'installments_plan' }
                        ].map(opt => (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => set('paymentType', opt.value)}
                                className={`flex-1 py-2.5 rounded-lg border-2 font-medium text-sm transition-all
                                    ${form.paymentType === opt.value
                                        ? isDarkMode ? 'border-red-500 bg-red-900/20 text-red-400' : 'border-red-500 bg-red-50 text-red-700'
                                        : isDarkMode ? 'border-zinc-600 text-zinc-400 hover:border-zinc-500' : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}
                            >
                                {t(opt.key)}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>{t('total_sale_price')} <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <span className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-zinc-400 text-sm`}>$</span>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={form.totalPrice}
                                    onChange={e => set('totalPrice', e.target.value)}
                                    placeholder={t('total_sale_price_placeholder')}
                                    className={`${inputClass} ${isRTL ? 'pr-7' : 'pl-7'}`}
                                    required
                                />
                            </div>
                        </div>

                        {form.paymentType === 'full' && (
                            <div>
                                <label className={labelClass}>{t('payment_date_label')} <span className="text-red-500">*</span></label>
                                <input
                                    type="date"
                                    value={form.firstPaymentDate}
                                    onChange={e => set('firstPaymentDate', e.target.value)}
                                    className={inputClass}
                                    required
                                />
                            </div>
                        )}

                        {form.paymentType === 'installments' && (
                            <div>
                                <label className={labelClass}>{t('num_installments')}</label>
                                <input
                                    type="number"
                                    min="2"
                                    max="120"
                                    value={form.numInstallments}
                                    onChange={e => setNumInstallments(e.target.value)}
                                    className={inputClass}
                                />
                            </div>
                        )}

                        {/* Individual installment date pickers */}
                        {form.paymentType === 'installments' && (
                            <div className="md:col-span-2">
                                <div className="flex items-center justify-between mb-3">
                                    <label className={`${labelClass} mb-0`}>{t('installment_schedule')}</label>
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={form.customAmounts}
                                            onChange={e => toggleCustomAmounts(e.target.checked)}
                                            className="w-4 h-4 accent-red-600"
                                        />
                                        <span className={`text-xs font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                                            {t('edit_installment_amounts', 'Edit amounts manually')}
                                        </span>
                                    </label>
                                </div>
                                <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-zinc-700' : 'border-zinc-200'}`}>
                                    <div className={`grid grid-cols-3 px-3 py-2 text-xs font-semibold ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-50 text-zinc-500'}`}>
                                        <span>#</span>
                                        <span>{t('due_date')}</span>
                                        <span>{t('amount')}</span>
                                    </div>
                                    <div className="divide-y divide-zinc-100 dark:divide-zinc-700 max-h-64 overflow-y-auto">
                                        {form.installmentDates.map((date, i) => {
                                            const autoAmt = form.totalPrice ? Math.round(parseFloat(form.totalPrice) / form.numInstallments) : 0;
                                            return (
                                                <div key={i} className={`grid grid-cols-3 items-center gap-3 px-3 py-2 ${i === 0 ? (isDarkMode ? 'bg-green-900/10' : 'bg-green-50/60') : ''}`}>
                                                    <span className={`font-mono font-bold text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{i + 1}</span>
                                                    <input
                                                        type="date"
                                                        value={date}
                                                        onChange={e => setInstallmentDate(i, e.target.value)}
                                                        className={`text-xs px-2 py-1.5 rounded-lg border ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-zinc-200' : 'bg-white border-zinc-200 text-zinc-800'} focus:ring-1 focus:ring-red-500 outline-none`}
                                                        required
                                                    />
                                                    {form.customAmounts ? (
                                                        <div className="relative">
                                                            <span className={`absolute ${isRTL ? 'right-2' : 'left-2'} top-1/2 -translate-y-1/2 text-zinc-400 text-xs`}>$</span>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="1"
                                                                value={form.installmentAmounts[i] ?? ''}
                                                                onChange={e => setInstallmentAmount(i, e.target.value)}
                                                                className={`w-full text-xs py-1.5 rounded-lg border ${isRTL ? 'pr-5 pl-2' : 'pl-5 pr-2'} ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-zinc-200' : 'bg-white border-zinc-200 text-zinc-800'} focus:ring-1 focus:ring-red-500 outline-none`}
                                                                required
                                                            />
                                                        </div>
                                                    ) : (
                                                        <span className={`text-sm font-semibold ${i === 0 ? 'text-green-600' : isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                                                            {autoAmt > 0 ? `$${autoAmt.toLocaleString()}` : '—'}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {form.customAmounts && (
                                        <div className={`flex items-center justify-between px-3 py-2 text-xs font-semibold border-t ${isDarkMode ? 'border-zinc-700' : 'border-zinc-200'}`}>
                                            <span className={isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}>
                                                {t('total_allocated', 'Total allocated')}: ${customAmountsTotal.toLocaleString()}
                                            </span>
                                            <span className={Math.abs(customAmountsRemaining) > 0.01 ? 'text-red-500' : 'text-green-600'}>
                                                {Math.abs(customAmountsRemaining) > 0.01
                                                    ? `${t('remaining', 'Remaining')}: $${customAmountsRemaining.toLocaleString()}`
                                                    : t('matches_total', 'Matches total price')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Mark as Paid Now */}
                        <div className={`col-span-1 md:col-span-2 mt-2 p-3.5 rounded-lg border-2 cursor-pointer select-none transition-all
                            ${form.markAsPaid
                                ? isDarkMode ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50'
                                : isDarkMode ? 'border-zinc-600 bg-zinc-800/50 hover:border-zinc-500' : 'border-zinc-200 bg-zinc-50 hover:border-zinc-300'}`}
                            onClick={() => set('markAsPaid', !form.markAsPaid)}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors
                                    ${form.markAsPaid
                                        ? 'border-green-500 bg-green-500'
                                        : isDarkMode ? 'border-zinc-500' : 'border-zinc-300'}`}
                                >
                                    {form.markAsPaid && (
                                        <svg viewBox="0 0 10 8" className="w-3 h-3 fill-white">
                                            <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <p className={`font-semibold text-sm ${form.markAsPaid ? 'text-green-600' : isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>
                                        {t('mark_as_paid_now')}
                                    </p>
                                    <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
                                        {form.paymentType === 'full' ? t('mark_as_paid_now_hint') : t('per_installment_paid_hint')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Section 4: Commission ── */}
                <div className={sectionClass}>
                    <label className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg border mb-4 ${isDarkMode ? 'border-zinc-600 bg-zinc-700 hover:bg-zinc-600' : 'border-zinc-200 bg-white hover:bg-zinc-50'} transition-colors`}>
                        <input
                            type="checkbox"
                            checked={form.hasCommission}
                            onChange={e => set('hasCommission', e.target.checked)}
                            className="w-4 h-4 accent-red-600"
                        />
                        <RiHandCoinLine className="text-red-500" size={18} />
                        <span className={`text-sm font-semibold ${isDarkMode ? 'text-zinc-100' : 'text-zinc-800'}`}>
                            {t('sale_has_commission') || 'Real estate company takes a commission on this sale'}
                        </span>
                    </label>

                    {form.hasCommission && (
                        <>
                            <div className="flex gap-3 mb-4">
                                {[
                                    { value: 'percent', key: 'commission_percent' },
                                    { value: 'fixed', key: 'commission_fixed_amount' }
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => set('commissionType', opt.value)}
                                        className={`flex-1 py-2.5 rounded-lg border-2 font-medium text-sm transition-all
                                            ${form.commissionType === opt.value
                                                ? isDarkMode ? 'border-red-500 bg-red-900/20 text-red-400' : 'border-red-500 bg-red-50 text-red-700'
                                                : isDarkMode ? 'border-zinc-600 text-zinc-400 hover:border-zinc-500' : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}
                                    >
                                        {t(opt.key) || (opt.value === 'percent' ? '% of price' : 'Fixed amount')}
                                    </button>
                                ))}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {form.commissionType === 'percent' ? (
                                    <div>
                                        <label className={labelClass}>{t('commission_rate') || 'Commission rate'} <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.1"
                                                value={form.commissionRate}
                                                onChange={e => set('commissionRate', e.target.value)}
                                                placeholder="e.g. 3"
                                                className={`${inputClass} ${isRTL ? 'pr-7' : 'pr-7'}`}
                                                required
                                            />
                                            <span className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-zinc-400 text-sm`}>%</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <label className={labelClass}>{t('commission_amount') || 'Commission amount'} <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <span className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-zinc-400 text-sm`}>$</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                value={form.commissionAmount}
                                                onChange={e => set('commissionAmount', e.target.value)}
                                                placeholder="0"
                                                className={`${inputClass} ${isRTL ? 'pr-7' : 'pl-7'}`}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <label className={labelClass}>{t('commission_notes') || 'Commission note (optional)'}</label>
                                    <input
                                        type="text"
                                        value={form.commissionNotes}
                                        onChange={e => set('commissionNotes', e.target.value)}
                                        placeholder={t('commission_notes_placeholder') || 'e.g. agency name / reason'}
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            {form.totalPrice && (
                                <div className={`mt-4 p-3.5 rounded-lg border flex items-center justify-between flex-wrap gap-2 ${isDarkMode ? 'border-zinc-600 bg-zinc-800/60' : 'border-zinc-200 bg-zinc-50'}`}>
                                    <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                        {t('commission_deducted_label') || 'Commission deducted from this sale'}
                                    </span>
                                    <span className="text-sm font-bold text-red-500">
                                        − ${computedCommissionAmount.toLocaleString()}
                                    </span>
                                    <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                        {t('net_to_company') || 'Net to company'}
                                    </span>
                                    <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                        ${netToCompanyPreview.toLocaleString()}
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/sales')}
                        className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-zinc-300' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700'}`}
                        disabled={saving}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                        {saving ? (
                            <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {t('saving')}</>
                        ) : (
                            <><RiSaveLine size={18} /> {t('record_sale')}</>
                        )}
                    </button>
                </div>
            </form>

            {/* Print Receipt Modal */}
            {showReceipt && createdSale && (
                <PrintReceipt
                    payment={createdSale}
                    type="sale"
                    onClose={() => { setShowReceipt(false); navigate('/admin/sales'); }}
                    autoPrint={true}
                />
            )}
        </div>
    );
};

export default SaleForm;
